import { IGif } from "services/types";
import { routes } from "../routes";
import { toast } from "react-hot-toast";

const getHost = () => {
  return window.location.host === "staging.redgifs.com" ? window.location.host : "www.redgifs.com";
};

export const copyLink = (link: string): Promise<void> => {
  return navigator.clipboard.writeText(link)
    .then(() => {
      toast.success("Link copied!");
    })
    .catch(() => {
      toast.error("Can't copy link");
    });
};

const getShareUser = (userId: string) => {
  return `https://${getHost()}/users/${userId}`;
};

export const copyUserLink = (userId: string): Promise<void> => {
  return copyLink(getShareUser(userId));
};

const getShareNiche = (niche: string) => {
  return `https://${getHost()}/niches/${niche}`;
};

export const copyNicheLink = (niche: string): Promise<void> => {
  return copyLink(getShareNiche(niche));
};

const getShareCollection = (userName: string, id: string): string => {
  return `https://${getHost()}${routes.collection(userName, id)}`;
};

export const copyCollectionLink = (userName: string, id: string): Promise<void> => {
  return copyLink(getShareCollection(userName, id));
};

const getGifLink = (id: string): string => {
  return `https://${getHost()}/watch/${id}`;
};

const getIframeUrl = (id: string) => {
  return `https://${getHost()}/ifr/${id}`;
};

export const getGifFixedEmbedCode = ({ id, width, height }: {id: string; width: number; height: number}) => {
  return [
    `<iframe src='${getIframeUrl(id)}' frameborder='0' scrolling='no' allowfullscreen width='${width}' height='${height}'></iframe>`,
    `<p><a href='${getGifLink(id)}'>via RedGIFs</a></p>`
  ].join("");
};

export const getGifResponsiveEmbedCode = ({ id, width, height }: {id: string; width: number; height: number}) => {
  const ratio = width / height;
  const percent = 100;
  const paddingBottom = (percent / ratio).toFixed(2);
  const divStyle = `position:relative; padding-bottom:${paddingBottom}%`;
  const iframeStyle = "position:absolute; top:0; left:0;";

  return [
    `<div style='${divStyle}'>`,
    `<iframe src='${getIframeUrl(id)}' frameBorder='0' scrolling='no' width='100%' height='100%' style='${iframeStyle}' allowFullScreen></iframe>`,
    "</div>",
    `<p><a href='${getGifLink(id)}'>via RedGIFs</a></p>`,
  ].join("");
};

export const getCollectionEmbedCode = (userName: string, id: string, name: string) => {
  return `<a href="${getShareCollection(userName, id)}">${name} collection</a>`;
};

export const copyGifLink = (gif: IGif): Promise<void> => {
  const link = gif.type !== 1 ? `https://i.redgifs.com/i/${gif.id}.jpg` : getGifLink(gif.id);
  return copyLink(link);
};

const sendToTwitter = (url: string): void => {
  window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}`, "_blank");
};

export const sendGifToTwitter = (id: string): void => {
  sendToTwitter(getGifLink(id));
};

export const sendCollectionToTwitter = (userName: string, id: string): void => {
  sendToTwitter(getShareCollection(userName, id));
};

const sendToReddit = (url: string, title?: string): void => {
  window.open(`https://www.reddit.com/submit?url=${encodeURIComponent(url)}&title=${title ?? ""}`, "_blank");
};

export const sendGifToReddit = (gif: IGif, galleryGifId?: string | null) => {
  const type = gif.type !== 1 ? "image" : "GIF";
  const userName = gif.userName ?? null;
  const tags = gif.tags || [];

  let title = "Please come up with a description...";

  if (tags.length > 0 && userName) {
    title = `${tags[0]} ${type} by ${userName}`;
  } else if (tags.length > 0) {
    title = `${tags[0]} ${type}`;
  } else if (userName) {
    title = `A porn ${type} by ${userName}`;
  }

  const shareLink = gif.type !== 1 ? `https://i.redgifs.com/i/${galleryGifId || gif.id}.jpg` : getGifLink(gif.id);

  sendToReddit(shareLink, title);
};

export const sendCollectionToReddit = (userName: string, id: string) => {
  sendToReddit(getShareCollection(userName, id));
};
